import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Field,
  Heading,
  HStack,
  IconButton,
  Link,
  NativeSelect,
  Spinner,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  Button as ButtonComponent,
  InputField,
  SectionCard,
} from '@themes/aegix';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  HiDownload,
  HiExternalLink,
  HiInformationCircle,
  HiUpload,
} from 'react-icons/hi';
import { HiPlus, HiTrash } from 'react-icons/hi2';
import { Link as RouterLink } from 'react-router-dom';
import { DrawerComponent } from '../../ui/drawerComponent';
import { toaster } from '../../ui/toaster';
import {
  addSamlRotationCert,
  createSamlConfig,
  deleteSamlConfig,
  deleteSamlRotationCert,
  extractClaimValuesFromMetadata,
  fetchSpMetadataXml,
  getAvailableRoles,
  getAvailableSchools,
  getSamlConfig,
  getSamlProviders,
  parseSpMetadataXml,
  testSamlSso,
  updateSamlConfig,
  type RoleMapping,
  type SamlConfigDetail,
  type SamlConfigWrite,
  type SamlProvider,
  type SchoolMapping,
} from '../../../services';
import { appLabel, deriveAppNameFromHost } from './appName';
import { CopyableRow } from './CopyableRow';
import { ImportCertDialog } from './ImportCertDialog';
import { ImportMetadataDialog } from './ImportMetadataDialog';
import { MappingCsvImport } from './MappingCsvImport';
import { MappingTable } from './MappingTable';
import { FormProvider, useForm } from 'react-hook-form';

const TYPE_CHOICES = [
  { value: 'saml', label: 'SAML 2.0' },
  { value: 'oidc', label: 'OIDC' },
];

// Generic SAML 2.0 runbook (used when the admin's IdP doesn't have a
// dedicated guide — Shibboleth, Auth0, OneLogin, JumpCloud, ADFS, etc.).
// Linked from the drawer header (i) icon. Served by the in-app admin-docs
// library; gated by `accounts.view_admin_guides`.
const GENERIC_SAML_RUNBOOK_URL = '/account/docs/saml-generic-integration-setup';

interface DraftState extends SamlConfigWrite {
  role_mappings: RoleMapping[];
  school_mappings: SchoolMapping[];
}

function emptyDraft(): DraftState {
  const host = typeof window !== 'undefined' ? window.location.host : '';
  return {
    name: '',
    user_hostname_match: '',
    app_name: deriveAppNameFromHost(host),
    type: 'saml',
    provider: null,
    idp_metadata_uri: '',
    sign_on_server_uri: '',
    idp_x509_cert_contents: '',
    site_assignment_name: 'groups',
    role_assignment_name: 'groups',
    add_hostname_in_entity_id: false,
    role_mappings: [],
    school_mappings: [],
  };
}

function hydrateDraft(detail: SamlConfigDetail): DraftState {
  return {
    name: detail.name,
    user_hostname_match: detail.user_hostname_match,
    app_name: detail.app_name,
    type: detail.type,
    provider: detail.provider ?? null,
    idp_metadata_uri: detail.idp_metadata_uri ?? '',
    sign_on_server_uri: detail.sign_on_server_uri ?? '',
    idp_x509_cert_contents: detail.idp_x509_cert_contents ?? '',
    site_assignment_name: detail.site_assignment_name,
    role_assignment_name: detail.role_assignment_name,
    add_hostname_in_entity_id: detail.add_hostname_in_entity_id,
    role_mappings: detail.role_mappings ?? [],
    school_mappings: detail.school_mappings ?? [],
  };
}

export interface SamlConfigDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** When non-null, drawer opens in edit mode and hydrates from this id. */
  configId: number | null;
}

export function SamlConfigDrawer({
  isOpen,
  onClose,
  configId,
}: SamlConfigDrawerProps) {
  const { t } = useTranslation();
  const isCreate = configId === null;
  const queryClient = useQueryClient();

  const detailQuery = useQuery({
    queryKey: ['sso', 'saml-configs', configId],
    queryFn: () => getSamlConfig(configId as number),
    enabled: isOpen && !isCreate && configId !== null,
  });

  const rolesQuery = useQuery({
    queryKey: ['sso', 'available-roles'],
    queryFn: getAvailableRoles,
    enabled: isOpen,
  });
  const schoolsQuery = useQuery({
    queryKey: ['sso', 'available-schools'],
    queryFn: getAvailableSchools,
    enabled: isOpen,
  });
  // ADR-136: IdP vendor catalog drives the provider selector + help routing.
  const providersQuery = useQuery({
    queryKey: ['sso', 'saml-providers'],
    queryFn: getSamlProviders,
    enabled: isOpen,
  });
  const providers: SamlProvider[] = providersQuery.data ?? [];

  const [draft, setDraft] = useState<DraftState>(() => emptyDraft());
  const [hydratedFor, setHydratedFor] = useState<number | null | 'create'>(
    null,
  );

  // ADR-136: the selected vendor routes the in-drawer help link to its guide
  // (falling back to the generic runbook) and gates the vendor-specific callout.
  const selectedProvider = useMemo(
    () => providers.find((p) => p.key === draft.provider) ?? null,
    [providers, draft.provider],
  );
  const helpUrl = selectedProvider?.guide_slug
    ? `/account/docs/${selectedProvider.guide_slug}`
    : GENERIC_SAML_RUNBOOK_URL;
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  // Raw IdP federation metadata XML from the last Import in this session.
  // Re-extracts claim values when site/role claim names change.
  // Never persisted; reset on drawer close.
  const [rawMetadataXml, setRawMetadataXml] = useState<string>('');
  const methods = useForm<DraftState>({
    defaultValues: emptyDraft(),
    mode: 'onBlur',
  });
  const {
    register,
    control,
    formState: { errors },
  } = methods;

  // RHF owns only name and user_hostname_match; every other field lives in `draft`.
  const onSubmit = (values: DraftState) => {
    saveMutation.mutate({
      ...draft,
      name: values.name,
      user_hostname_match: values.user_hostname_match,
    });
  };
  useEffect(() => {
    if (!isOpen) return;
    if (isCreate && hydratedFor !== 'create') {
      const init = emptyDraft();
      setDraft(init);
      setHydratedFor('create');
      methods.reset(init);
      return;
    }
    if (!isCreate && detailQuery.data && hydratedFor !== configId) {
      const detailDraft = hydrateDraft(detailQuery.data);
      setDraft(detailDraft);
      setHydratedFor(configId);
      methods.reset(detailDraft);
    }
  }, [isOpen, isCreate, configId, detailQuery.data, hydratedFor, methods]);

  useEffect(() => {
    if (!isOpen) {
      setHydratedFor(null);
      setRawMetadataXml('');
    }
  }, [isOpen]);

  // Prepopulate the Site mapping table from <Attribute Name="<site-claim>">
  // values declared in the imported IdP metadata. Fires on import and again
  // when the site claim name changes. Preserves any existing school_name
  // selection for group_names that match; drops rows whose group_name is no
  // longer in the metadata. No-op when the metadata has no values for the
  // configured claim, so typing a partial / unmatched claim name doesn't
  // destroy prior table contents.
  useEffect(() => {
    if (!rawMetadataXml) return;
    const claimName = (draft.site_assignment_name ?? '').trim();
    if (!claimName) return;
    const values = extractClaimValuesFromMetadata(rawMetadataXml, claimName);
    if (values.length === 0) return;
    setDraft((d) => {
      const existingByName = new Map(
        d.school_mappings.map((m) => [m.group_name, m]),
      );
      const synced: SchoolMapping[] = values.map(
        (v) => existingByName.get(v) ?? { group_name: v, school_name: '' },
      );
      return { ...d, school_mappings: synced };
    });
  }, [rawMetadataXml, draft.site_assignment_name]);

  // Same pattern for the Role mapping table, keyed off the role claim name.
  useEffect(() => {
    if (!rawMetadataXml) return;
    const claimName = (draft.role_assignment_name ?? '').trim();
    if (!claimName) return;
    const values = extractClaimValuesFromMetadata(rawMetadataXml, claimName);
    if (values.length === 0) return;
    setDraft((d) => {
      const existingByName = new Map(
        d.role_mappings.map((m) => [m.group_name, m]),
      );
      const synced: RoleMapping[] = values.map(
        (v) => existingByName.get(v) ?? { group_name: v, role_name: '' },
      );
      return { ...d, role_mappings: synced };
    });
  }, [rawMetadataXml, draft.role_assignment_name]);

  const saveMutation = useMutation({
    mutationFn: async (updatedDraft?: DraftState) => {
      const currentDraft = updatedDraft ?? draft;
      if (isCreate) return createSamlConfig(currentDraft);
      return updateSamlConfig(configId as number, currentDraft);
    },
    onSuccess: () => {
      toaster.create({
        title: isCreate
          ? t('sso.samlConfigCreated', 'SAML configuration created')
          : t('common.saved', 'Saved'),
        type: 'success',
        duration: 4000,
      });
      queryClient.invalidateQueries({ queryKey: ['sso', 'saml-configs'] });
      // SCIM list shows linked SAML names — refresh that too.
      queryClient.invalidateQueries({ queryKey: ['sso', 'scim-configs'] });
      onClose();
    },
    onError: (err) => {
      toaster.create({
        title: t('sso.saveFailed', 'Save failed'),
        description: (err as Error).message,
        type: 'error',
        duration: 8000,
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteSamlConfig(configId as number),
    onSuccess: () => {
      toaster.create({
        title: t('sso.samlConfigDeleted', 'SAML configuration deleted'),
        type: 'success',
        duration: 4000,
      });
      queryClient.invalidateQueries({ queryKey: ['sso', 'saml-configs'] });
      queryClient.invalidateQueries({ queryKey: ['sso', 'scim-configs'] });
      onClose();
    },
    onError: (err) => {
      toaster.create({
        title: t('sso.deleteFailed', 'Delete failed'),
        description: (err as Error).message,
        type: 'error',
        duration: 8000,
      });
    },
  });

  const handleDelete = () => {
    const ok = window.confirm(
      t(
        'sso.deleteSamlConfirmMsg',
        'Delete SAML configuration "{{name}}"? This drops the IdP metadata, all group→role/site mappings, and any rotation certs. Cannot be undone.',
        { name: draft.name || '' },
      ),
    );
    if (ok) deleteMutation.mutate();
  };

  const loading = !isCreate && detailQuery.isLoading;
  const hasMetadata = Boolean(
    draft.idp_metadata_uri ||
    draft.sign_on_server_uri ||
    draft.idp_x509_cert_contents,
  );

  return (
    <>
      <DrawerComponent
        isOpen={isOpen}
        onClose={onClose}
        title={
          isCreate
            ? t('sso.addSigninConfig', 'Add Sign-in Configuration')
            : t('sso.editSigninConfig', 'Edit Sign-in Configuration')
        }
        titleAdornment={
          <Link
            asChild
            aria-label="Open SAML setup guide"
            color="text.secondary"
            _hover={{ color: 'text.primary' }}
            display="inline-flex"
            alignItems="center"
            fontSize="lg"
            data-e2e="sso-saml-help-link"
          >
            <RouterLink to={helpUrl}>
              <HiInformationCircle />
            </RouterLink>
          </Link>
        }
        size="md"
        closeOnOutside={false}
        closeOnEsc={false}
        footer={
          <HStack width="100%" gap={4}>
            {!isCreate && configId !== null ? (
              <>
                <ButtonComponent
                  visual="alert"
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  icon={<HiTrash />}
                  flex={1}
                  data-e2e="sso-saml-delete"
                >
                  {deleteMutation.isPending
                    ? t('common.deleting', 'Deleting…')
                    : t('common.delete', 'Delete')}
                </ButtonComponent>
                <TestSsoFooterButton configId={configId} />
              </>
            ) : null}
            <ButtonComponent visual="secondary" onClick={onClose} flex={1}>
              {t('common.cancel', 'Cancel')}
            </ButtonComponent>
            <ButtonComponent
              visual="primary"
              disabled={saveMutation.isPending}
              flex={1}
              data-e2e="sso-saml-save"
              form="saml-config-form"
              type="submit"
            >
              {saveMutation.isPending
                ? t('common.saving', 'Saving…')
                : isCreate
                  ? t('common.create', 'Create')
                  : t('common.save', 'Save')}
            </ButtonComponent>
          </HStack>
        }
      >
        {loading ? (
          <Box py={8}>
            <Spinner /> <Text ml={3}>{t('sso.loadingConfig', 'Loading configuration…')}</Text>
          </Box>
        ) : detailQuery.isError ? (
          <Text color="red.600">
            {t('sso.failedLoadConfig', 'Failed to load configuration:')} {(detailQuery.error as Error).message}
          </Text>
        ) : (
          <FormProvider {...methods}>
            <form
              id="saml-config-form"
              onSubmit={methods.handleSubmit(onSubmit)}
            >
              <VStack align="stretch" gap={4}>
                <SectionCard title={t('sso.application', 'Application')}>
                  <VStack align="stretch" gap={3}>
                    <Field.Root invalid={!!errors.name}>
                      <Field.Label htmlFor="saml-name">
                        {t('sso.displayName', 'Display name')}
                      </Field.Label>
                      <InputField
                        id="saml-name"
                        data-e2e="sso-saml-name"
                        placeholder={t('sso.displayNamePlaceholder', 'e.g. Acme Okta SAML')}
                        {...register('name', {
                          required: t('sso.displayNameRequired', 'Display name is required'),
                          // TODO: Retire this setDraft shadow state once the rest of the drawer
                          // is migrated to react-hook-form (methods.watch or methods.getValues).
                          onChange: (e) =>
                            setDraft((d) => ({ ...d, name: e.target.value })),
                        })}
                        aria-describedby={
                          errors.name ? 'saml-name-error' : undefined
                        }
                      />
                      {errors.name && (
                        <Field.ErrorText
                          id="saml-name-error"
                          fontSize="sm"
                          data-e2e="saml-name-error"
                        >
                          {errors.name.message}
                        </Field.ErrorText>
                      )}
                    </Field.Root>

                    <Field.Root invalid={!!errors.user_hostname_match}>
                      <Field.Label htmlFor="saml-hostname">
                        {t('sso.emailDomain', 'Email domain')}
                      </Field.Label>
                      <InputField
                        id="saml-hostname"
                        data-e2e="sso-saml-hostname"
                        placeholder="acme.com"
                        {...register('user_hostname_match', {
                          required: t('sso.emailDomainRequired', 'Email domain is required'),
                          // TODO: Retire this setDraft shadow state once the rest of the drawer
                          // is migrated to react-hook-form (methods.watch or methods.getValues).
                          onChange: (e) =>
                            setDraft((d) => ({
                              ...d,
                              user_hostname_match: e.target.value,
                            })),
                        })}
                        aria-describedby={
                          errors.user_hostname_match
                            ? 'saml-hostname-error'
                            : undefined
                        }
                      />
                      {errors.user_hostname_match && (
                        <Field.ErrorText
                          id="saml-hostname-error"
                          fontSize="sm"
                          data-e2e="saml-hostname-error"
                        >
                          {errors.user_hostname_match.message}
                        </Field.ErrorText>
                      )}
                    </Field.Root>

                    <HStack gap={3}>
                      <Field.Root flex={1}>
                        <Field.Label htmlFor="saml-app">{t('sso.target', 'Target')}</Field.Label>
                        <InputField
                          id="saml-app"
                          data-e2e="sso-saml-app"
                          value={appLabel(draft.app_name ?? '')}
                          readOnly
                        />
                      </Field.Root>
                      <Field.Root flex={1}>
                        <Field.Label htmlFor="saml-type">{t('sso.protocol', 'Protocol')}</Field.Label>
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            id="saml-type"
                            title={t('sso.protocol', 'Protocol')}
                            aria-label={t('sso.protocol', 'Protocol')}
                            value={draft.type ?? ''}
                            onChange={(e) =>
                              setDraft((d) => ({
                                ...d,
                                type: e.target.value || null,
                              }))
                            }
                          >
                            {TYPE_CHOICES.map((c) => (
                              <option key={c.value} value={c.value}>
                                {c.label}
                              </option>
                            ))}
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </Field.Root>
                    </HStack>

                    {/* ADR-136: IdP vendor selector — drives the SAML
                        handshake + per-vendor help link + callouts. */}
                    <Field.Root>
                      <Field.Label htmlFor="saml-provider">
                        {t('sso.identityProvider', 'Identity provider')}
                      </Field.Label>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          id="saml-provider"
                          title={t('sso.identityProvider', 'Identity provider')}
                          aria-label={t('sso.identityProvider', 'Identity provider')}
                          data-e2e="sso-saml-provider"
                          value={draft.provider ?? ''}
                          onChange={(e) =>
                            setDraft((d) => ({
                              ...d,
                              provider: e.target.value || null,
                            }))
                          }
                        >
                          <option value="">{t('sso.selectProviderPlaceholder', 'Select a provider…')}</option>
                          {providers.map((p) => (
                            <option key={p.key} value={p.key}>
                              {p.name}
                            </option>
                          ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                      <Field.HelperText fontSize="xs" color="gray.500">
                        {t(
                          'sso.identityProviderHelper',
                          'Tunes the SAML handshake (NameID/account hint and group-value format) and links the matching setup guide.',
                        )}
                      </Field.HelperText>
                    </Field.Root>

                    {selectedProvider?.key === 'classlink' && (
                      <Box
                        data-e2e="sso-provider-callout-classlink"
                        bg="surface.input"
                        borderRadius="md"
                        p={3}
                      >
                        <Text fontSize="sm">
                          <strong>ClassLink:</strong>{' '}
                          {t(
                            'sso.classlinkCallout',
                            "the user's email must be the NameID. Comma-joined group claims are handled automatically — map the bare group names in the Roles / Sites tables below.",
                          )}
                        </Text>
                      </Box>
                    )}
                    {selectedProvider?.key === 'adfs' && (
                      <Box
                        data-e2e="sso-provider-callout-adfs"
                        bg="surface.input"
                        borderRadius="md"
                        p={3}
                      >
                        <Text fontSize="sm">
                          <strong>Microsoft AD FS:</strong>{' '}
                          {t(
                            'sso.adfsCallout',
                            'group values may arrive domain-qualified (e.g. CONTOSO\\Responders). Enter the bare group name in the Roles / Sites tables — case and the CONTOSO\\ prefix are normalized at login.',
                          )}
                        </Text>
                      </Box>
                    )}
                  </VStack>
                </SectionCard>

                {/* Aegix SP details — values the admin gives to the IdP. */}
                <TiakiSpDetailsSection
                  userHostnameMatch={draft.user_hostname_match ?? ''}
                />

                {/* Identity Provider — read-only listing + Import button */}
                <SectionCard
                  title={t('sso.identityProvider', 'Identity Provider')}
                  action={
                    <ButtonComponent
                      type="button"
                      visual="secondary"
                      size="sm"
                      onClick={() => setImportDialogOpen(true)}
                      icon={<HiUpload />}
                      data-e2e="sso-saml-import-metadata"
                    >
                      {t('common.import', 'Import')}
                    </ButtonComponent>
                  }
                >
                  {hasMetadata ? (
                    <VStack
                      align="stretch"
                      gap={2}
                      borderWidth="1px"
                      borderColor="border.line"
                      borderRadius="md"
                      p={3}
                      data-e2e="sso-saml-metadata-readout"
                    >
                      <Box>
                        <Text
                          textStyle="captionEmphasized"
                          color="text.secondary"
                        >
                          Entity ID
                        </Text>
                        <Text
                          fontSize="sm"
                          fontFamily="mono"
                          wordBreak="break-all"
                        >
                          {draft.idp_metadata_uri || (
                            <Text as="span" color="text.secondary">
                              —
                            </Text>
                          )}
                        </Text>
                      </Box>
                      <Box>
                        <Text
                          textStyle="captionEmphasized"
                          color="text.secondary"
                        >
                          Sign-On URL
                        </Text>
                        <Text
                          fontSize="sm"
                          fontFamily="mono"
                          wordBreak="break-all"
                        >
                          {draft.sign_on_server_uri || (
                            <Text as="span" color="text.secondary">
                              —
                            </Text>
                          )}
                        </Text>
                      </Box>
                      <Box>
                        <Text
                          textStyle="captionEmphasized"
                          color="text.secondary"
                        >
                          Signing certificate (X.509)
                        </Text>
                        <Text
                          fontSize="xs"
                          fontFamily="mono"
                          color="text.primary"
                          wordBreak="break-all"
                        >
                          {draft.idp_x509_cert_contents
                            ? `${(draft.idp_x509_cert_contents || '').slice(0, 80)}…`
                            : '—'}
                        </Text>
                      </Box>
                    </VStack>
                  ) : (
                    <Box
                      borderWidth="1px"
                      borderStyle="dashed"
                      borderColor="border.secondary"
                      borderRadius="md"
                      p={4}
                      textAlign="center"
                    >
                      <Text fontSize="sm" color="text.secondary">
                        {t(
                          'sso.noMetadataImportedNotice',
                          'No metadata imported yet. Click Import Metadata to upload XML, paste XML, or enter the values manually.',
                        )}
                      </Text>
                    </Box>
                  )}
                </SectionCard>

                {/* Shared "Import" CSV button — same control on both the Sites
                and Roles table headers (the CSV import is cross-cutting,
                seeding both role and site mappings in one upload). */}
                {(() => {
                  const csvImportButton = !isCreate ? (
                    <ButtonComponent
                      type="button"
                      visual="secondary"
                      size="sm"
                      onClick={() => setCsvImportOpen(true)}
                      icon={<HiUpload />}
                      data-e2e="sso-saml-csv-import-open"
                    >
                      {t('common.import', 'Import')}
                    </ButtonComponent>
                  ) : null;
                  return (
                    <>
                      {/* Sites: claim name field + group→site mapping table. */}
                      <MappingTable
                        mode="sites"
                        claimName={draft.site_assignment_name ?? ''}
                        onClaimNameChange={(v) =>
                          setDraft((d) => ({ ...d, site_assignment_name: v }))
                        }
                        headerExtra={csvImportButton}
                        roleMappings={draft.role_mappings}
                        schoolMappings={draft.school_mappings}
                        availableRoles={rolesQuery.data ?? []}
                        availableSchools={schoolsQuery.data ?? []}
                        onRoleMappingsChange={(v) =>
                          setDraft((d) => ({ ...d, role_mappings: v }))
                        }
                        onSchoolMappingsChange={(v) =>
                          setDraft((d) => ({ ...d, school_mappings: v }))
                        }
                      />

                      {/* Roles: claim name field + group→role mapping table. */}
                      <MappingTable
                        mode="roles"
                        claimName={draft.role_assignment_name ?? ''}
                        onClaimNameChange={(v) =>
                          setDraft((d) => ({ ...d, role_assignment_name: v }))
                        }
                        headerExtra={csvImportButton}
                        roleMappings={draft.role_mappings}
                        schoolMappings={draft.school_mappings}
                        availableRoles={rolesQuery.data ?? []}
                        availableSchools={schoolsQuery.data ?? []}
                        onRoleMappingsChange={(v) =>
                          setDraft((d) => ({ ...d, role_mappings: v }))
                        }
                        onSchoolMappingsChange={(v) =>
                          setDraft((d) => ({ ...d, school_mappings: v }))
                        }
                      />
                    </>
                  );
                })()}

                {!isCreate && detailQuery.data ? (
                  <RotationCertSection config={detailQuery.data} />
                ) : null}
              </VStack>
            </form>
          </FormProvider>
        )}
      </DrawerComponent>

      <ImportMetadataDialog
        isOpen={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        initialValues={{
          entity_id: draft.idp_metadata_uri ?? '',
          sso_url: draft.sign_on_server_uri ?? '',
          cert: draft.idp_x509_cert_contents ?? '',
        }}
        onApply={(values) => {
          setDraft((d) => ({
            ...d,
            idp_metadata_uri: values.entity_id,
            sign_on_server_uri: values.sso_url,
            idp_x509_cert_contents: values.cert,
          }));
          setRawMetadataXml(values.raw_xml);
        }}
      />

      {!isCreate && configId !== null ? (
        <MappingCsvImport
          mode="saml"
          configId={configId}
          open={csvImportOpen}
          onClose={() => setCsvImportOpen(false)}
        />
      ) : null}
    </>
  );
}

// ── Test SSO footer button (extracted from the old in-body
//    TestSsoSection — moved to the drawer footer per the latest UX).

function TestSsoFooterButton({ configId }: { configId: number }) {
  const { t } = useTranslation();
  const [testing, setTesting] = useState(false);

  const handleTestSso = async () => {
    setTesting(true);
    try {
      const result = await testSamlSso(configId);
      if (result.success && result.redirect_url) {
        window.open(result.redirect_url, '_blank', 'noopener,noreferrer');
        toaster.create({
          title: t('sso.testSsoOpened', 'Test SSO opened'),
          description: t(
            'sso.testSsoOpenedDesc',
            'Complete the IdP login in the new tab. If you return signed in, the configuration is correct.',
          ),
          type: 'success',
          duration: 8000,
        });
      } else {
        toaster.create({
          title: t('sso.testSsoFailed', 'Test SSO failed'),
          description: result.message,
          type: 'error',
          duration: 10000,
        });
      }
    } catch (err) {
      toaster.create({
        title: t('sso.testSsoRequestFailed', 'Test SSO request failed'),
        description: (err as Error).message,
        type: 'error',
        duration: 8000,
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <ButtonComponent
      visual="secondary"
      onClick={handleTestSso}
      disabled={testing}
      icon={<HiExternalLink />}
      flex={1}
      data-e2e="sso-saml-test-sso"
    >
      {testing ? t('sso.testing', 'Testing…') : t('sso.testSso', 'Test SSO')}
    </ButtonComponent>
  );
}

// ── Rotation cert section (table layout with +Add cert / per-row
//    Import + Delete; modal Upload/Paste flow via ImportCertDialog). ──

function RotationCertSection({ config }: { config: SamlConfigDetail }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  /** null = closed. number = replace this cert id. 'add' = add a new one. */
  const [importTarget, setImportTarget] = useState<number | 'add' | null>(null);

  const refetch = () =>
    queryClient.invalidateQueries({
      queryKey: ['sso', 'saml-configs', config.id],
    });

  const addCert = useMutation({
    mutationFn: (cert: string) => addSamlRotationCert(config.id, cert),
    onSuccess: () => {
      refetch();
      toaster.create({
        title: t('sso.rotationCertAdded', 'Rotation cert added'),
        type: 'success',
        duration: 5000,
      });
    },
    onError: (err) => {
      toaster.create({
        title: t('sso.addRotationCertFailed', 'Add rotation cert failed'),
        description: (err as Error).message,
        type: 'error',
        duration: 8000,
      });
    },
  });

  const deleteCert = useMutation({
    mutationFn: (certId: number) => deleteSamlRotationCert(config.id, certId),
    onSuccess: () => {
      refetch();
      toaster.create({
        title: t('sso.rotationCertRemoved', 'Rotation cert removed'),
        type: 'success',
        duration: 4000,
      });
    },
    onError: (err) => {
      toaster.create({
        title: t('sso.removeFailed', 'Remove failed'),
        description: (err as Error).message,
        type: 'error',
        duration: 8000,
      });
    },
  });

  // Replace = delete the existing cert then add the new one. The two
  // requests are sequenced so the new cert only persists if the old one
  // was successfully removed (avoids exceeding the per-config cert cap).
  const replaceCert = async (certId: number, certText: string) => {
    await deleteSamlRotationCert(config.id, certId);
    await addSamlRotationCert(config.id, certText);
    refetch();
    toaster.create({
      title: t('sso.rotationCertReplaced', 'Rotation cert replaced'),
      type: 'success',
      duration: 5000,
    });
  };

  const atCap = config.rotation_certs.length >= 1;
  const columnHeaderProps = {
    color: 'text.secondary',
    textStyle: 'captionEmphasized' as const,
    borderBottom: '1px solid',
    borderRight: '1px solid',
    _last: { borderRight: 'none' },
    borderColor: 'border.secondary',
    textTransform: 'capitalize' as const,
    textAlign: 'left' as const,
  };
  const bodyRowProps = {
    borderBottom: '1px solid var(--color-border)',
    color: 'text.primary',
    border: 'none',
  } as const;
  const bodyCellProps = {
    color: 'text.primary',
    border: 'none',
  } as const;

  return (
    <>
      <SectionCard
        e2eTitle="sso-saml-rotation-certs"
        header={
          <>
            <Heading as="h3" textStyle="h2" color="text.primary">
              {t('sso.rotationCertificates', 'Rotation certificates')}
            </Heading>
            <ButtonComponent
              type="button"
              visual="secondary"
              size="sm"
              onClick={() => setImportTarget('add')}
              disabled={atCap}
              icon={<HiPlus />}
              data-e2e="sso-saml-rotation-cert-add"
            >
              {t('sso.addCert', 'Add cert')}
            </ButtonComponent>
          </>
        }
      >
        <Table.Root>
          <Table.Header bg="surface.input">
            <Table.Row borderTop="1px solid" borderColor="border.secondary">
              <Table.ColumnHeader {...columnHeaderProps}>
                {t('sso.certificate', 'Certificate')}
              </Table.ColumnHeader>
              <Table.ColumnHeader
                {...columnHeaderProps}
                width="120px"
                aria-label="Row actions"
              />
            </Table.Row>
          </Table.Header>
          <Table.Body color="text.primary" pb="15px">
            {config.rotation_certs.length === 0 ? (
              <Table.Row {...bodyRowProps}>
                <Table.Cell
                  {...bodyCellProps}
                  colSpan={2}
                  textAlign="center"
                  color="text.secondary"
                  py={8}
                >
                  {t('sso.noRotationCertsNotice', 'No rotation certs yet. Click "Add cert" to upload or paste one.')}
                </Table.Cell>
              </Table.Row>
            ) : (
              config.rotation_certs.map((cert) => (
                <Table.Row key={cert.id} {...bodyRowProps}>
                  <Table.Cell
                    {...bodyCellProps}
                    fontFamily="mono"
                    fontSize="xs"
                  >
                    {cert.cert_excerpt}
                  </Table.Cell>
                  <Table.Cell {...bodyCellProps} textAlign="right">
                    <HStack gap={1} justify="flex-end">
                      <ButtonComponent
                        type="button"
                        visual="secondary"
                        size="sm"
                        onClick={() => setImportTarget(cert.id)}
                        icon={<HiUpload />}
                        data-e2e={`sso-saml-rotation-cert-replace-${cert.id}`}
                      >
                        {t('common.import', 'Import')}
                      </ButtonComponent>
                      <IconButton
                        aria-label="Remove rotation cert"
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteCert.mutate(cert.id)}
                        loading={deleteCert.isPending}
                        data-e2e={`sso-saml-rotation-cert-delete-${cert.id}`}
                      >
                        <HiTrash />
                      </IconButton>
                    </HStack>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </SectionCard>

      <ImportCertDialog
        isOpen={importTarget !== null}
        onClose={() => setImportTarget(null)}
        pending={addCert.isPending}
        title={
          importTarget === 'add'
            ? t('sso.addRotationCert', 'Add rotation cert')
            : t('sso.replaceRotationCert', 'Replace rotation cert')
        }
        onApply={async (certText) => {
          if (importTarget === 'add') {
            await addCert.mutateAsync(certText);
          } else if (typeof importTarget === 'number') {
            try {
              await replaceCert(importTarget, certText);
            } catch (err) {
              toaster.create({
                title: t('sso.replaceRotationCertFailed', 'Replace rotation cert failed'),
                description: (err as Error).message,
                type: 'error',
                duration: 8000,
              });
            }
          }
        }}
      />
    </>
  );
}

// ── Aegix SP details subsection ───────────────────────────────────────
//
// Surfaces the ACS URL + SP Entity ID + downloadable SP metadata XML for
// the email domain the admin just typed in. Drives off the existing
// `/saml/metadata/?hnm=<domain>` endpoint — no saved SAMLConfig required,
// so this works during the create flow before the IdP-side fields are
// filled in.

function TiakiSpDetailsSection({
  userHostnameMatch,
}: {
  userHostnameMatch: string;
}) {
  const { t } = useTranslation();
  const hostname = userHostnameMatch.trim();
  const enabled = hostname.length > 0;

  const metadataQuery = useQuery({
    queryKey: ['sso', 'sp-metadata-preview', hostname],
    queryFn: () => fetchSpMetadataXml(hostname),
    enabled,
    staleTime: 60_000,
  });

  const parsed = useMemo(() => {
    if (!metadataQuery.data) return null;
    try {
      const { sp_entity_id, acs_url } = parseSpMetadataXml(metadataQuery.data);
      return { ok: true as const, sp_entity_id, acs_url };
    } catch (err) {
      return { ok: false as const, error: (err as Error).message };
    }
  }, [metadataQuery.data]);

  const handleDownload = () => {
    if (!metadataQuery.data) return;
    const url = window.URL.createObjectURL(
      new Blob([metadataQuery.data], { type: 'text/xml' }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = `tiaki-sp-metadata-${hostname || 'config'}.xml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toaster.create({
      title: t('sso.metadataDownloaded', 'Metadata downloaded'),
      description: t(
        'sso.metadataDownloadedDesc',
        'Upload this file to your IdP to register Tiaki as the SP.',
      ),
      type: 'success',
      duration: 4000,
    });
  };

  return (
    <SectionCard title={t('sso.aegixServiceProvider', 'Aegix Service Provider')} e2eTitle="sso-saml-sp-details">
      {!enabled ? (
        <Box
          borderWidth="1px"
          borderStyle="dashed"
          borderColor="border.secondary"
          borderRadius="md"
          p={4}
          textAlign="center"
        >
          <Text fontSize="sm" color="text.secondary">
            {t(
              'sso.enterEmailDomainNotice',
              'Enter an email domain above to see your Aegix SP details and download metadata.',
            )}
          </Text>
        </Box>
      ) : metadataQuery.isLoading ? (
        <HStack>
          <Spinner size="sm" />
          <Text fontSize="sm" color="text.secondary">
            {t('sso.loadingSpDetails', 'Loading SP details…')}
          </Text>
        </HStack>
      ) : metadataQuery.isError ? (
        <Text fontSize="sm" color="surface.danger">
          {t('sso.failedLoadSpDetails', 'Failed to load SP details:')} {(metadataQuery.error as Error).message}
        </Text>
      ) : parsed && !parsed.ok ? (
        <Text fontSize="sm" color="surface.danger">
          {t('sso.failedParseSpMetadata', 'Failed to parse SP metadata:')} {parsed.error}
        </Text>
      ) : parsed && parsed.ok ? (
        <VStack align="stretch" gap={3}>
          <CopyableRow
            label="Entity ID"
            value={parsed.sp_entity_id}
            fieldId="entity-id"
          />
          <CopyableRow
            label="Assertion Consumer Service (ACS) URL"
            value={parsed.acs_url}
            fieldId="acs-url"
          />
          <HStack gap={3} pt={1}>
            <ButtonComponent
              type="button"
              visual="secondary"
              onClick={handleDownload}
              icon={<HiDownload />}
              data-e2e="sso-saml-sp-download-metadata"
            >
              {t('common.export', 'Export')}
            </ButtonComponent>
          </HStack>
        </VStack>
      ) : null}
    </SectionCard>
  );
}

export default SamlConfigDrawer;
